/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';

export type EditableTitleTextFieldProps = {
    /**
     * The initial value to populate the field with.
     */
    initialValue: string;

    /**
     * A callback for when the value is to be submitted
     * back to the caller.
     */
    onSubmit: (dto: EditableTitleTextFieldDto) => void;

    /**
     * A callback for when the field changes are to be
     * discarded and edit mode closed.
     */
    onCancel: () => void;
};

export type EditableTitleTextFieldDto = {
    /**
     * The value that is being submitted.
     */
    value: string;

    /**
     * A flag indicating whether the value was changed
     * from the initial value passed to this component
     * or not.
     */
    changed: boolean;
};

const messages = defineMessages({
    fieldPlaceholder: {
        id: 'core.editableTitle.fieldPlaceholder',
        defaultMessage: 'Enter title here ...'
    },
    acceptButtonAriaLabel: {
        id: 'core.editableTitle.acceptButtonAriaLabel',
        defaultMessage: 'Accept'
    }
});

const editableTitleTheme = getScopedTheme('EditableTitle');

const textFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({
    field: {
        fontSize: editableTitleTheme.fontSize,
        padding: 0,
        selectors: { '&::placeholder': { fontSize: editableTitleTheme.fontSize } }
    },
    fieldGroup: {
        height: editableTitleTheme.height
    }
});

/**
 * This is a text field that is to be used inside the EditableTitle
 * component, and it tracks the last initial value passed to it
 * in order to provide a check of whether the last initial value
 * is the same as the value after the user applied edits to the
 * text field.
 *
 * @param props The input to the text field.
 */
export const EditableTitleTextField = (props: EditableTitleTextFieldProps) => {
    const { initialValue, onSubmit, onCancel } = props;
    const { localize } = useLocalization();
    const { formState, updateFormState, formStateDto } = useFormState<{ fieldData: string }>({
        fieldData: [initialValue, FormValidators.createRequiredValidator(), FormValidators.createBlacklistValidator()]
    });

    /**
     * Checks if the value is valid or not, and if it is sends it
     * back to the caller along with a flag that indicates whether this
     * value is new or did the value remain the same from the last time
     * the input field was open.
     */
    const submitValue = () => {
        if (!formState.form.valid) {
            return;
        }

        const trimmedValue = formStateDto.fieldData.trim();

        onSubmit({
            value: trimmedValue,
            changed: trimmedValue !== initialValue.trim()
        });
    };

    /**
     * Submits the value of the text field if the enter key was
     * pressed and signals the parent to close the input field
     * and the edit mode when the escape key is pressed.
     *
     * @param event The event fired by the keypress.
     */
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
            submitValue();
        } else if (event.key === 'Escape') {
            onCancel();
        }
    };

    return (
        <>
            <LuisTextField
                autoFocus
                underlined
                styles={textFieldStyles}
                placeholder={localize(messages.fieldPlaceholder)}
                value={formState.fields.fieldData.value}
                errorMessage={formState.fields.fieldData.errorMessage}
                onChange={(_, fieldData) => updateFormState({ fieldData })}
                onKeyDown={onKeyDown}
            />

            <IconButton
                onClick={submitValue}
                disabled={!formState.form.valid}
                iconProps={{ iconName: 'Accept' }}
                ariaLabel={localize(messages.acceptButtonAriaLabel)}
            />
        </>
    );
};
